import { useEffect, useMemo, useState } from 'react';
import { C, FONTS } from '../../tokens/design';
import Shell from '../../components/layout/Shell';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { ScrollText } from 'lucide-react';
import { useApiClient } from '../../api/apiClient';

const CATEGORY_OPTIONS = ['all', 'auth', 'scan', 'finding', 'team', 'client', 'settings'];

function valueOf(entry, name) {
  const camel = name.charAt(0).toLowerCase() + name.slice(1);
  return entry?.[camel] ?? entry?.[name] ?? '';
}

function fmtTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function fmtCategory(value) {
  return String(value || 'activity').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function AuditLog() {
  const api = useApiClient();
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ entries: [], total: 0, pageSize: 50 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const path = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: '50' });
    if (category !== 'all') params.set('category', category);
    return `/msp/audit-log?${params.toString()}`;
  }, [category, page]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.get(path);
      setData({
        entries: result?.entries ?? result?.Entries ?? [],
        total: result?.total ?? result?.Total ?? 0,
        pageSize: result?.pageSize ?? result?.PageSize ?? 50,
      });
    } catch (err) {
      setError(err.message || 'Unable to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [path]);

  const totalPages = Math.max(1, Math.ceil((data.total || data.entries.length || 0) / (data.pageSize || 50)));

  return (
    <Shell title="Audit Log" sub="Complete activity history for your organization">
      <Card style={{ padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <ScrollText size={18} color={C.sky} strokeWidth={1.75} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans }}>Partner Activity Trail</div>
          <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Risk acceptance, scans, account changes, and configuration activity</div>
        </div>
        <select value={category} onChange={e => { setCategory(e.target.value); setPage(1); }} style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '6px 9px', color: C.t1, fontSize: 11, fontFamily: FONTS.mono, cursor: 'pointer' }}>
          {CATEGORY_OPTIONS.map(o => <option key={o} value={o}>{o === 'all' ? 'All Activity' : fmtCategory(o)}</option>)}
        </select>
        <Button variant="sec" style={{ fontSize: 10 }} onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</Button>
      </Card>

      {error && (
        <Card style={{ padding: 16, marginBottom: 12, borderLeft: `3px solid ${C.red}` }}>
          <div style={{ fontSize: 12, color: C.red, fontFamily: FONTS.mono }}>Audit log unavailable: {error}</div>
        </Card>
      )}

      <Card>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.b1}` }}>
              {['Time', 'Actor', 'Category', 'Action', 'Detail', 'Client'].map(h => (
                <th key={h} style={{ padding: '8px 12px', fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'left', background: C.s2 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: C.t2, fontSize: 11, fontFamily: FONTS.mono }}>Loading audit activity...</td></tr>
            )}
            {!loading && data.entries.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center' }}>
                <ScrollText size={28} color={C.t3} strokeWidth={1.5} style={{ marginBottom: 10 }} />
                <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans, marginBottom: 4 }}>No activity found</div>
                <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>Try a different category or refresh after making a change.</div>
              </td></tr>
            )}
            {!loading && data.entries.map((entry, index) => {
              const id = valueOf(entry, 'EntryId') || index;
              return (
                <tr key={id} style={{ borderBottom: `1px solid ${C.b2}` }}>
                  <td style={{ padding: '9px 12px', fontSize: 10, color: C.t2, fontFamily: FONTS.mono, whiteSpace: 'nowrap' }}>{fmtTime(valueOf(entry, 'Timestamp'))}</td>
                  <td style={{ padding: '9px 12px', fontSize: 11, color: C.t1, fontFamily: FONTS.mono }}>{valueOf(entry, 'ActorEmail') || '-'}</td>
                  <td style={{ padding: '9px 12px' }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: C.sky, background: `${C.sky}14`, border: `1px solid ${C.sky}33`, borderRadius: 4, padding: '2px 7px', fontFamily: FONTS.mono }}>{fmtCategory(valueOf(entry, 'Category'))}</span>
                  </td>
                  <td style={{ padding: '9px 12px', fontSize: 11, color: C.t1, fontFamily: FONTS.mono }}>{valueOf(entry, 'Action') || '-'}</td>
                  <td style={{ padding: '9px 12px', fontSize: 11, color: C.t2, fontFamily: FONTS.sans, lineHeight: 1.4, maxWidth: 480 }}>{valueOf(entry, 'Detail') || '-'}</td>
                  <td style={{ padding: '9px 12px', fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{valueOf(entry, 'ClientId') || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ padding: '8px 14px', borderTop: `1px solid ${C.b1}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{data.entries.length} of {data.total || data.entries.length} events</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Button variant="sec" style={{ fontSize: 10, padding: '4px 8px' }} disabled={loading || page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>{'\u2190'} Prev</Button>
            <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Page {page} of {totalPages}</span>
            <Button variant="sec" style={{ fontSize: 10, padding: '4px 8px' }} disabled={loading || page >= totalPages} onClick={() => setPage(p => p + 1)}>Next {'\u2192'}</Button>
          </div>
        </div>
      </Card>
    </Shell>
  );
}
